<template>
  <div class="d-flex align-items-center">
    <span
      v-if="showPreStock"
      class="ml-4 mr-4 text-secondary text-center"
      style="width: 60px; word-wrap: break-word"
      >{{ disabled ? "???" : formatThousandsSeparator(currentStock) }}</span
    >
    <i v-if="showPreStock" class="fas fa-arrow-right mr-2 text-secondary"></i>
    <div class="input-wrapper">
      <textarea
        ref="stockInput"
        inputmode="numeric"
        class="form-control no-arrow text-center custom-textarea"
        :class="{
          'is-invalid': invalid,
        }"
        style="
          resize: none;
          overflow: hidden;
          word-break: break-all;
          min-height: 38px;
        "
        :style="{ maxWidth: inputMaxWidth }"
        :value="disabled ? '???' : displayAfterStock"
        :max="max"
        :min="min"
        @focus="onFocus"
        @input="onInputArea"
        @blur="onBlur"
        :disabled="disabled"
        @keydown="allowOnlyTwoDecimalNumber($event)"
        rows="1"
      ></textarea>
      <svg
        v-if="invalid && showErrorIcon"
        class="input-invalid-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#dc3545"
        stroke-width="2"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
        />
        <line
          x1="12"
          y1="8"
          x2="12"
          y2="13"
        />
        <circle
          cx="12"
          cy="16.5"
          r="0.5"
          fill="#dc3545"
          stroke="none"
        />
      </svg>
    </div>
  </div>
</template>

<script>
import "@/theme/modal/ProductAndStock/Product.scss";
import BigNumber from "bignumber.js";
import {
  validateTwoDecimal,
  formatThousandsSeparator,
} from "@/utils/numberValidation";

export default {
  name: "PreAndPostStockField",
  components: {},
  props: {
    currentStock: {
      type: Number,
      default: 0,
    },
    adjustStock: {
      type: Number,
      default: 0,
    },
    afterStockValue: {
      type: [Number, String],
      default: null,
    },
    max: {
      type: Number,
      default: 9999999999.99,
    },
    min: {
      type: Number,
      default: -9999999999.99,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    showErrorIcon: {
      type: Boolean,
      default: true,
    },
    inputMaxWidth: {
      type: String,
      default: "100px",
    },
    showPreStock: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      observer: null,
      isEditing: false,
      userHasEdited: false,
      afterStock:
        this.afterStockValue != null
          ? Number(this.afterStockValue)
          : Number(
              new BigNumber(this.currentStock)
                .plus(new BigNumber(this.adjustStock))
                .toNumber()
            ),
    };
  },
  validations: {},
  computed: {
    displayAfterStock() {
      if (this.isEditing) return String(this.afterStock ?? "");
      return formatThousandsSeparator(this.afterStock);
    },
  },
  mounted() {
    this.autoResize();
    if (window.IntersectionObserver && this.$refs.stockInput) {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          this.autoResize();
        }
      });
      this.observer.observe(this.$refs.stockInput);
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    onFocus() {
      this.isEditing = true;
    },
    onInputArea(e) {
      const typed = e.target.value.replace(/,/g, "");
      // Replace "-0" with "-" so typing minus on a zero clears it
      if (typed === "-0" || typed === "-0.") {
        this.afterStock = "-";
        this.$refs.stockInput.value = "-";
        return;
      }
      this.onInputChange(typed);
      this.autoResize();
    },
    autoResize() {
      this.$nextTick(() => {
        const target = this.$refs.stockInput;
        if (target) {
          target.style.height = "38px";
          if (target.scrollHeight > 38) {
            target.style.height = target.scrollHeight + 2 + "px";
          }
        }
      });
    },
    formatThousandsSeparator(value) {
      return formatThousandsSeparator(value);
    },
    onInputChange(value) {
      this.userHasEdited = true;
      const rawString = (value ?? "").toString().replace(/,/g, "");
      this.afterStock = rawString;

      const raw = rawString.trim();
      if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
        this.onChange(new BigNumber(0).toFixed(2));
        return;
      }

      const afterNum = new BigNumber(raw);
      if (!afterNum.isFinite()) return;

      const current = new BigNumber(this.currentStock || 0);

      const adjustment = afterNum.minus(current).toNumber();

      this.onChange(adjustment);
    },
    onBlur() {
      this.isEditing = false;
      this.userHasEdited = false;
      let raw = (this.afterStock ?? "").toString().trim();

      if (raw === "") {
        const defaultAfter = new BigNumber(this.currentStock).plus(0);
        this.afterStock = !validateTwoDecimal(defaultAfter)
          ? defaultAfter.toFixed(2)
          : defaultAfter.toNumber();
        this.onChange(new BigNumber(0));
        return;
      }

      //  -0, -0.0, 0.0, 00.00, 000, 000.000
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);
      if (isZeroLike) {
        this.afterStock = "0";
        this.onChange(
          new BigNumber(this.afterStock)
            .minus(new BigNumber(this.currentStock))
            .toFixed(2)
        );
        return;
      }

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) {
        const defaultAfter = new BigNumber(this.currentStock).plus(
          new BigNumber(this.adjustStock).toFixed(2)
        );

        this.afterStock = !validateTwoDecimal(defaultAfter)
          ? defaultAfter.toFixed(2)
          : defaultAfter.toNumber();

        this.onChange(
          new BigNumber(this.afterStock)
            .minus(new BigNumber(this.currentStock))
            .toFixed(2)
        );
        return;
      }

      if (this.afterStock < this.min) {
        this.afterStock = new BigNumber(this.min).toNumber();
      } else if (this.afterStock > this.max) {
        this.afterStock = new BigNumber(this.max).toNumber();
      } else if (!validateTwoDecimal(this.afterStock)) {
        this.afterStock = new BigNumber(this.afterStock).toFixed(2);
      }
      this.onChange(
        new BigNumber(this.afterStock)
          .minus(new BigNumber(this.currentStock))
          .toFixed(2)
      );
    },
    allowOnlyTwoDecimalNumber(e) {
      if (e.ctrlKey || e.metaKey) {
        const k = (e.key || "").toLowerCase();
        const allowed = ["a", "c", "v", "x", "z", "y"];
        if (allowed.includes(k)) return;
      }
      if (e.shiftKey && e.key === "Insert") return;

      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];

      if (allowedKeys.includes(e.key)) return;

      const input = e.target;
      const value = input.value || "";
      const { selectionStart, selectionEnd } = input;

      const nextValue =
        value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
      const rawNextValue = nextValue.replace(/,/g, "");

      // Minus: only at the very start
      if (e.key === "-") {
        if (selectionStart !== 0) {
          e.preventDefault();
        }
        return;
      }

      // One decimal point only
      if (e.key === ".") {
        if (value.includes(".")) {
          e.preventDefault();
        }
        return;
      }

      // Digits 0–9: allow only if result would have at most 2 decimal places
      if (/^\d$/.test(e.key)) {
        const newValue =
          value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
        const parts = newValue.replace(/,/g, "").split(".");
        if (parts.length === 2 && parts[1].length > 2) {
          e.preventDefault();
        }
        return;
      }
      e.preventDefault();
    },
  },
  watch: {
    afterStockValue(newVal) {
      if (this.isEditing) return;
      if (newVal != null) {
        this.afterStock = Number(newVal);
      }
      this.autoResize();
    },
    adjustStock(newVal) {
      // Don't recalculate if afterStockValue prop is provided
      if (this.afterStockValue != null) return;
      if (this.isEditing) return;

      if (this.afterStock === "") return;

      const result = new BigNumber(this.currentStock).plus(
        new BigNumber(newVal)
      );
      const raw = String(this.afterStock).trim();
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);

      if (isZeroLike && this.userHasEdited) {
        if (raw.startsWith("-")) {
          return;
        }
        this.afterStock = 0;
        return;
      }

      this.userHasEdited = false;
      if (!validateTwoDecimal(result)) {
        this.afterStock = result.toFixed(2);
      } else {
        this.afterStock = result.toNumber();
      }
      this.autoResize();
    },
    currentStock() {
      // Don't recalculate if afterStockValue prop is provided
      if (this.afterStockValue != null) return;
      if (this.isEditing) return;

      if (this.afterStock === "") return;

      const result = new BigNumber(this.currentStock).plus(
        new BigNumber(this.adjustStock)
      );
      const raw = String(this.afterStock).trim();
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);

      if (isZeroLike && this.userHasEdited) {
        if (raw.startsWith("-")) {
          return;
        }
        this.afterStock = 0;
        return;
      }

      this.userHasEdited = false;
      if (!validateTwoDecimal(result)) {
        this.afterStock = result.toFixed(2);
      } else {
        this.afterStock = result.toNumber();
      }
      this.autoResize();
    },
  },
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
  display: inline-flex;
}

.input-invalid-icon {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
  width: 20px;
  height: 20px;
}

.no-arrow::-webkit-inner-spin-button,
.no-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-arrow {
  -moz-appearance: textfield;
}

.was-validated .form-control:invalid,
.form-control.is-invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}

::v-deep .form-control:disabled {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
}

::v-deep .form-control:disabled::placeholder {
  color: #c6c6c6 !important;
}

.custom-textarea {
  line-height: 1.5;
  padding-top: 6px;
  padding-bottom: 6px;
}
</style>
